import React, {useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import ReactSelect from 'react-select';

import {LoadingIndicator} from '../components/LoadingIndicator';
import {NavUserMaintenance} from '../components/NavUserMaintenance';
import {Flow, FlowFetchResponse} from '../_services/Flow';

import {Modal} from '../components/Modal';
import {User} from '../_services/User';
import {Alert} from '../components/Alert';
import {ActivityButton} from '../components/ActivityButton';

type DropdownMaintenancePageProps = {};

type BulkEditModalState = {
    show: boolean,
    choices: string
}

const bulkEditModalInitialState = {
    show: false,
    choices: ''
}

export const DropdownMaintenance: React.FunctionComponent<DropdownMaintenancePageProps> = ({}) => {
    const {workflowId} = useParams();
    const history = useHistory();
    // TODO - array of refs to support tabbing to the value field
    const newValueInputRef = useRef<HTMLTextAreaElement>(null);

    const [pageIsLoading, setPageIsLoading]: any = useState(false);
    const [errorMessage, setErrorMessage]: any = useState('');
    const [form, setForm] = useState<any>();
    const [formFields, setFormFields] = useState<any>([]);
    const [selectedFormField, _setSelectedFormField] = useState<any>();
    const [newValueState, setNewValueState] = useState<any>({isPasting: false, value: ''});
    const [showValues, setShowValues] = useState<any>(false);
    const [bulkEditModalState, setBulkEditModalState] = useState<BulkEditModalState>({...bulkEditModalInitialState});
    const [isSaving, setIsSaving] = useState<boolean>(false);

    // get the form
    useEffect(() => {
        setPageIsLoading(true);
        Flow.getInstance().getForm(workflowId, '?__include_steps=1')
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setForm(response.json.form);
                } else {
                    throw Error(response?.json?.message || 'Error fetching Workflow');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching Workflow');
            })
            .finally(() => {
                setPageIsLoading(false);
            });
    }, [workflowId]);

    // set the editable form fields
    useEffect(() => {
        if (form) {
            if (!(User.getInstance().isIsWfo() || (form?.flow_form_admin_role && User.getInstance().hasRole(form.flow_form_admin_role)))) {
                history.replace('/');
            }

            setFormFields(
                form.fields.map((field) => {
                    // get all fields with editable values (i.e. select, multiselect, etc...)
                    if (['select', 'multiselect'].includes(field.type)) {
                        // check if field is an autocomplete field
                        const hasEtlConfig = Object.keys(field.etlConfig).length > 0;

                        // check if field is referenced in any other fields visibility rules
                        const driveVisibilityLogicFor = form.fields.filter((f) => (
                            f?.conditionalLogic?.rules?.filter(r => r.fieldId === field.id.toString())?.length > 0 ||
                            f?.flowConditionalLogic?.filter(l => l?.rules?.filter(r => r.fieldId === field.id.toString())?.length > 0).length > 0
                        ));
                        const doesDriveVisibilityLogic = driveVisibilityLogicFor?.length > 0;

                        // check if field is referenced in any step conditional logic
                        const driveStepLogicFor = Object.values(form.steps).filter(s => (
                            s?.feed_condition_conditional_logic_object?.conditionalLogic?.rules?.filter(r => r.fieldId === field.id.toString())?.length > 0
                        ));
                        const doesDriveStepLogic = driveStepLogicFor?.length > 0;

                        return {
                            id: field.id,
                            name: field.label,
                            type: field.type,
                            inputs: field.inputs,
                            choices: field.choices,
                            label: field.label,
                            text: field.label,
                            value: field.id,
                            isAutocomplete: hasEtlConfig,
                            canEdit: !hasEtlConfig && !doesDriveVisibilityLogic && !doesDriveStepLogic,
                            doesDriveVisibilityLogic,
                            doesDriveStepLogic,
                            driveVisibilityLogicFor,
                            driveStepLogicFor
                        };
                    }

                    return false;
                }).filter(Boolean)
            );
        }
    }, [form]);

    /**
     * When pasting formatted text (fields tab-delimited, lines CR/LF-delimited), this function handles
     * parsing and saving the lines
     * @param pastedValue
     * @param stepAttributes
     */
    const handlePaste = (pastedValue: string) => {
        const lines = pastedValue.split(/\r?\n/g);
        const data = lines.map((line: string) => {
            return {
                text: line,
                value: line,
                isSelected: false,
                price: ''
            }
        });

        setSelectedFormField((state) => ({
            ...state,
            choices: [
                ...state.choices,
                ...data
            ]
        }));
        setNewValueState((state) => ({isPasting: false, value: ''}));
    };

    // override default state setter to add some additional logic
    const setSelectedFormField = (state) => {

        // if state.isDirty exists, then it will be set to true, otherwise set to false
        _setSelectedFormField(() => ({
            ...state,
            isDirty: (state && state.hasOwnProperty('isDirty'))
        }));

        // if any of the values differ, setShowValues to true
        state && state?.choices && setShowValues((state.choices.filter((c) => c.text !== c.value).length > 0));
    }

    const handleSave = () => {

        setIsSaving(true);
        _setSelectedFormField((state) => ({
            ...state,
            canEdit: false
        }));
        Flow.getInstance().saveDropdownValues(form, selectedFormField)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    _setSelectedFormField((state) => ({
                        ...state,
                        isDirty: false
                    }));
                    setForm(response.json.form);
                } else {
                    throw Error(response?.json?.message || 'Error saving Dropdown Values');
                }
            })
            .catch(error => {
                alert(error);
            })
            .finally(() => {
                setIsSaving(false);
                _setSelectedFormField((state) => ({
                    ...state,
                    canEdit: true
                }));
            })
    }

    if (!form) {
        return <LoadingIndicator/>;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    {form && <h1 className={'abb-header'}>{form.title}</h1>}
                    <h2>Dropdown Values</h2>
                </div>
                <div className="col-12">
                    {workflowId && <NavUserMaintenance workflowId={workflowId}/>}
                </div>
                <div className="col-12 p-4">
                    {errorMessage && <p className="alert alert-danger">{errorMessage}</p>}
                    {pageIsLoading && <LoadingIndicator/>}

                    <Alert type={'info'}
                           message={<>Updates to Dropdown Values do not affect existing or historical workflows. If data updates are required, please enter a support request using the <em>Support</em> menu above</>}
                           icon={'info-circle fa-2x mr-2'}
                    />

                    <div><strong>Select a dropdown from the list below</strong></div>

                    <div className="row justify-content-between">
                        <div className="col-6">
                            <ReactSelect
                                placeholder="Select a Field..."
                                required={true}
                                isClearable={true}
                                options={formFields}
                                value={selectedFormField}
                                onChange={(option) => {
                                    if (selectedFormField?.isDirty) {
                                        if (confirm('You have unsaved changes. Click "OK" to discard changes and switch to the new field')) {
                                            setSelectedFormField(option);
                                        }
                                    } else {
                                        setSelectedFormField(option);
                                    }
                                }}
                            />
                        </div>

                        <div className="col-6 d-flex justify-content-end">
                            <ActivityButton type="button"
                                            isActive={isSaving}
                                            disabled={!selectedFormField?.isDirty || !selectedFormField.canEdit}
                                            className="btn btn-primary"
                                            onClick={() => handleSave()}
                            >Save Dropdown Values
                            </ActivityButton>
                        </div>
                    </div>
                </div>

                {selectedFormField && selectedFormField?.id && <>
                    {(selectedFormField.doesDriveVisibilityLogic || selectedFormField.doesDriveStepLogic) &&
                        <div className="col-12">
                            <Alert type={'danger'}
                                   message={<>Dropdowns that drive the visibility of other fields or used in workflow step conditions cannot be edited. Enter a support request using the <em>Support</em> menu above</>}
                                   icon={'exclamation-triangle fa-2x mr-2'}
                            />
                        </div>
                    }
                    {selectedFormField.driveVisibilityLogicFor.length > 0 &&
                        <div className="col-12">
                            <Alert type={'warning'}
                                   message={<>
                                       <div><strong>The following fields are dependent on <em>{selectedFormField.label}</em></strong></div>
                                       <ul style={{listStyle: 'none'}} className="m-0 p-0">
                                           {selectedFormField.driveVisibilityLogicFor.map(f => <li>{f.label}</li>)}
                                       </ul>
                                   </>}
                            />
                        </div>
                    }
                    {selectedFormField.driveStepLogicFor.length > 0 &&
                        <div className="col-12">
                            <Alert type={'warning'}
                                   message={<>
                                       <div><strong>The following workflow steps are dependent on <em>{selectedFormField.label}</em></strong></div>
                                       <ul style={{listStyle: 'none'}} className="m-0 p-0">
                                           {selectedFormField.driveStepLogicFor.map(s => <li>{s.step_name}</li>)}
                                       </ul>
                                   </>}
                            />
                        </div>
                    }
                    {selectedFormField.isAutocomplete &&
                        <div className="col-12">
                            <Alert type={'danger'}
                                   message={<>Autocomplete dropdowns get their data from an external source and cannot be edited. Enter a support request using the <em>Support</em> menu above</>}
                                   icon={'exclamation-triangle fa-2x mr-2'}
                            />
                        </div>
                    }

                    {selectedFormField.canEdit &&
                        <div className="col-12">
                            <ul>
                                <li>To add new dropdown values, enter the new value in the box at the bottom of the page and either hit the <em>Enter</em> key on your keyboard or click <em>Add</em> to the right of the input box.</li>
                                <li>Delete dropdown values using the <em>Delete</em> link on the row next to the value</li>
                                <li>To insert / delete multiple values at once or to rearrange values, click the <em>Bulk Edit</em> link above the table of values</li>
                                <li>Click the radio button to the left of the value to set the default selected value for the dropdown. If no default value is set, the first value will be used</li>
                                <li>If you wish to have the no initial selection in the dropdown, use the <em>Bulk Edit</em> button to set an empty value as the first option</li>
                                <li><strong>After all changes have been made, click <em>Save Dropdown Values</em> above to save your changes</strong></li>
                            </ul>
                        </div>
                    }

                    <div className="col-12">
                        <div className="d-flex justify-content-between">
                            <button type="button"
                                    disabled={!selectedFormField.canEdit}
                                    className="btn btn-sm btn-link"
                                    onClick={() => {
                                        // format choices for bulk edit modal
                                        const choices = selectedFormField.choices.map(c => (
                                            c.text !== c.value ? `${c.text}|${c.value}` : c.text
                                        )).join('\n');

                                        setBulkEditModalState({...bulkEditModalInitialState, show: true, choices})
                                    }}
                            >Bulk Edit
                            </button>
                            <div className="form-check">
                                <input className="form-check-input"
                                       type="checkbox"
                                       id="show-values"
                                       checked={showValues}
                                       disabled={!selectedFormField.canEdit}
                                       onChange={(e) => {
                                           const isSelected = e.target.checked;
                                           if (isSelected === false) {
                                               // copy choice.text to choice.value
                                           }
                                           setShowValues(isSelected);
                                       }}
                                />
                                <label className="form-check-label" htmlFor="show-values">
                                    Show Values
                                </label>
                            </div>
                        </div>
                        <table className="table table-sm">
                            <thead>
                            <tr>
                                {selectedFormField.canEdit &&
                                    <th style={{width: '25px', whiteSpace: 'nowrap'}}>Default Selected</th>
                                }
                                <th style={{width: showValues ? '45%' : '90%'}}>Label</th>
                                {showValues && <th style={{width: '45%'}}>Value</th>}
                                {selectedFormField.canEdit &&
                                    <th style={{width: '100px', whiteSpace: 'nowrap'}}>Actions</th>
                                }
                            </tr>
                            </thead>
                            <tbody>
                            {selectedFormField?.choices?.map((c, index) => (
                                <tr>
                                    {selectedFormField.canEdit &&
                                        <td style={{whiteSpace: 'nowrap', textAlign: 'center'}}>
                                            <div className="form-check">
                                                <input className="form-check-input"
                                                       type="radio"
                                                       name="selected"
                                                       id={c.value}
                                                       checked={c.isSelected}
                                                       onChange={(e) => {
                                                           const isSelected = e.target.checked;
                                                           console.log(selectedFormField.choices);
                                                           setSelectedFormField({
                                                               ...selectedFormField,
                                                               choices: [
                                                                   ...selectedFormField.choices.slice(0, index).map(c => ({
                                                                       ...c,
                                                                       isSelected: false
                                                                   })),
                                                                   {
                                                                       ...c,
                                                                       isSelected: isSelected
                                                                   },
                                                                   ...selectedFormField.choices.slice(index + 1).map(c => ({
                                                                       ...c,
                                                                       isSelected: false
                                                                   }))
                                                               ]
                                                           });
                                                       }}
                                                />
                                            </div>
                                        </td>
                                    }
                                    <td>
                                        <label className="form-check-label" htmlFor={c.value}>
                                            {c.text}
                                        </label>
                                    </td>
                                    {showValues &&
                                        <td>{c.value}</td>
                                    }
                                    {selectedFormField.canEdit &&
                                        <td style={{whiteSpace: 'nowrap'}}>
                                            <button type="button"
                                                    className="btn btn-sm btn-link text-danger"
                                                    onClick={() => {
                                                        setSelectedFormField({
                                                            ...selectedFormField,
                                                            choices: [
                                                                ...selectedFormField.choices.slice(0, index),
                                                                ...selectedFormField.choices.slice(index + 1)
                                                            ]
                                                        });
                                                    }}
                                            >Delete
                                            </button>
                                        </td>
                                    }
                                </tr>
                            ))}
                            {selectedFormField.canEdit &&
                                <tr>
                                    <td/>
                                    <td>
                                        <textarea rows={1}
                                                  className="form-control"
                                                  style={{height: 'calc(1.8em + .75rem + 2px)'}}
                                                  disabled={false}
                                                  ref={newValueInputRef}
                                                  value={newValueState.value}
                                                  onChange={(e) => {
                                                      if (newValueState.isPasting) {
                                                          handlePaste(e.target.value);
                                                      } else {
                                                          const v = e.target.value;
                                                          setNewValueState((state) => ({
                                                              ...state,
                                                              value: v
                                                          }));
                                                      }
                                                  }}
                                                  onPaste={() => {
                                                      return;
                                                      setNewValueState((state) => ({
                                                          ...state,
                                                          isPasting: true
                                                      }));
                                                  }}
                                                  onKeyDown={(e) => {
                                                      const v = e.target.value;
                                                      if (e.keyCode === 13) {
                                                          setSelectedFormField({
                                                              ...selectedFormField,
                                                              choices: [
                                                                  ...selectedFormField.choices,
                                                                  {
                                                                      text: v,
                                                                      value: v,
                                                                      isSelected: false,
                                                                      price: ''
                                                                  }
                                                              ]
                                                          });

                                                          setNewValueState((state) => ({isPasting: false, value: ''}));
                                                          // clear the entered newline
                                                          // not ideal, but works in a pinch
                                                          setTimeout(() => {
                                                              setNewValueState((state) => ({isPasting: false, value: ''}));
                                                          }, 100);
                                                      }
                                                  }}
                                        />
                                    </td>
                                    {showValues && <td>
                                        <input type="text"
                                               className="form-control"
                                        />
                                    </td>}
                                    <td>
                                        <button type="button"
                                                className="btn btn-sm btn-link"
                                                onClick={() => {
                                                    if (newValueState.value) {
                                                        setSelectedFormField({
                                                            ...selectedFormField,
                                                            choices: [
                                                                ...selectedFormField.choices,
                                                                {
                                                                    text: newValueState.value,
                                                                    value: newValueState.value,
                                                                    isSelected: false,
                                                                    price: ''
                                                                }
                                                            ]
                                                        });
                                                        setNewValueState((state) => ({isPasting: false, value: ''}));
                                                    }
                                                }}
                                        >Add
                                        </button>
                                    </td>
                                </tr>
                            }
                            </tbody>
                        </table>
                    </div>
                </>}
            </div>
            {bulkEditModalState.show &&
                <Modal size={'lg'}>
                    <div className="modal-header">
                        <h5 className="modal-title">Bulk Edit</h5>
                        <button type="button"
                                className="close"
                                aria-label="Close"
                                tabIndex={-1}
                                onClick={() => setBulkEditModalState({...bulkEditModalInitialState})}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <p>Enter dropdown values 1 per line. Click <strong>Save</strong> to apply changes.</p>
                        <textarea rows={20}
                                  className="form-control"
                                  value={bulkEditModalState.choices}
                                  onChange={(e) => {
                                      const v = e.target.value;
                                      setBulkEditModalState((state) => ({
                                          ...state,
                                          choices: v
                                      }))
                                  }}
                        />
                    </div>
                    <div className="modal-footer">
                        <button type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setBulkEditModalState({...bulkEditModalInitialState});
                                }}
                        >Cancel
                        </button>
                        <button type="button"
                                className="btn btn-primary"
                                onClick={() => {

                                    const lines = bulkEditModalState.choices.split(/\r?\n/g);
                                    const choices = lines.map((line: string) => {
                                        const parts = line.split(/\|/);
                                        return {
                                            text: parts[0],
                                            value: parts.length > 1 ? parts[1] : parts[0],
                                            isSelected: false,
                                            price: ''
                                        }
                                    });

                                    setSelectedFormField({
                                        ...selectedFormField,
                                        choices
                                    });
                                    setBulkEditModalState({...bulkEditModalInitialState});
                                }}
                        >Save
                        </button>
                    </div>
                </Modal>
            }
        </>
    );
};